<template>
  <div>
    <v-row v-if="!loading" no-gutters>
      <v-col cols="12">
        <v-card class="mt-4">
          <v-card-title class="third white--text font-weight-bold">
            <span class="ml-2">Informations du Cavalier</span>
            <v-spacer />
            <span>{{ lastUpdateDate }}</span>
            <v-btn icon class="ml-2" @click="refreshLicense">
              <v-icon size="28" color="white" class="">mdi-refresh</v-icon>
            </v-btn>
            <v-btn icon class="ml-2" @click="createLicense">
              <v-icon size="28" color="white" class="">mdi-plus</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-row no-gutters>
              <v-col cols="12" class="px-2">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Numéro de licence</v-list-item-subtitle>
                    <v-list-item-title v-if="equestrianLicense && equestrianLicense.data.pegase_license_code">{{ equestrianLicense.data.pegase_license_code }}</v-list-item-title>
                    <v-list-item-title v-else>--</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="6" class="px-2">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Date de début de licence</v-list-item-subtitle>
                    <v-list-item-title v-if="equestrianLicense && equestrianLicense.data.pegase_license_year">{{ equestrianLicense.data.start_date }}</v-list-item-title>
                    <v-list-item-title v-else>--</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
              <v-col cols="6" class="px-2">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Date de fin de licence</v-list-item-subtitle>
                    <v-list-item-title v-if="equestrianLicense && equestrianLicense.data.pegase_license_year">{{ equestrianLicense.data.end_date }}</v-list-item-title>
                    <v-list-item-title v-else>--</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-skeleton-loader
      v-else
      class="mx-auto"
      max-width="790"
      type="card"
    />
    <ConfirmationModal
      btn-label="actualisation"
      btn-icon="mdi-content-save"
      style="z-index: 100000;"
      title="Licence"
      :activator.sync="showConfirmationModal"
      :close-modal="closeConfirmationModal"
      :action="updateLicense"
    >
      <template #body>
        <h3 class="black--text">
          Le client possède déjà une licence. Voulez-vous le mettre à jour ?
        </h3>
      </template>
    </ConfirmationModal>
  </div>
</template>

<script>
import { ref, computed, onBeforeMount, watch } from '@vue/composition-api';
import { startOfYear, endOfYear, format } from 'date-fns';
import { formatDate, sendNotification } from '@/helpers/utils';
import { refreshCustomerLicense, createCustomerLicense, renewCustomerLicense, getCustomerLicense } from '@/helpers/request';
import ConfirmationModal from '../../common/ConfirmationModal.vue';

export default {
  components: { ConfirmationModal },
  props: {
    customer: {
      type: Object,
      default: null
    }
  },
  setup (props) {
    const customerObject = ref(props.customer);
    const equestrianLicense = ref(null);
    const loading = ref(false);
    const showConfirmationModal = ref(false);

    // make request to get licenses
    const getCustomerEquestrianLicense = async () => {
      loading.value = true;
      const response = await getCustomerLicense(customerObject.value.uuid);

      if (response.success && response.data && response.data.licenses && response.data.licenses) {
        const license = response.data.licenses[0];
        if (license) {
          const date = `${license.data.pegase_license_year}-01-01`;
          equestrianLicense.value = {
            ...license,
            lastUpdateTime: format(new Date(license.data.lastUpdateTime), 'T'),
            data: {
              ...license.data,
              start_date: format(startOfYear(new Date(date)), 'dd-MM-yyyy'),
              end_date: format(endOfYear(new Date(date)), 'dd-MM-yyyy')
            }
          };
        }
      }
      loading.value = false;
    };

    const lastUpdateDate = computed(() => {
      let LASTUPDATEDATE = '--';
      let LASTUPDATETIME = '--';
      if (equestrianLicense.value && equestrianLicense.value.lastUpdateTime) {
        const { lastUpdateTime } = equestrianLicense.value;
        LASTUPDATEDATE = format(new Date(parseFloat(lastUpdateTime)), 'dd/MM/yyyy');
        LASTUPDATETIME = format(new Date(parseFloat(lastUpdateTime)), "HH'h'mm");
      }
      return `Dernier refresh le: ${LASTUPDATEDATE}  à ${LASTUPDATETIME}`;
    });

    const computeLicenseResponseBody = response => {
      const date = `${response.data.pegase_license_year}-01-01`;

      return {
        ...response.data,
        data: {
          ...response.data,
          start_date: format(startOfYear(new Date(date)), 'dd-MM-yyyy'),
          end_date: format(endOfYear(new Date(date)), 'dd-MM-yyyy')
        }
      };
    };

    const refreshLicense = async () => {
      loading.value = true;
      const response = await refreshCustomerLicense(customerObject.value.uuid);

      const notification = { code: 'error', message: 'Error synchronize license' };

      if (response.success) {
        notification.code = 'success';
        notification.message = "L'activation de votre licence FFE est en cours. Revenez plus tard pour vérifier son statut. Merci de votre patience !";
        // equestrianLicense.value = computeLicenseResponseBody(response);
      }

      loading.value = false;
      sendNotification(notification.code, notification.message);
    };

    onBeforeMount(async () => {
      await getCustomerEquestrianLicense();
    });

    const createLicense = async () => {
      if (equestrianLicense.value) {
        showConfirmationModal.value = true;
        return;
      }
      // create it
      loading.value = true;
      const response = await createCustomerLicense(customerObject.value.uuid);
      const notification = { code: 'error', message: 'Erreur lors de la création de la licence' };

      if (response.success) {
        notification.code = 'success';
        notification.message = 'Licence créée avec succès';
        equestrianLicense.value = computeLicenseResponseBody(response);
      }
      loading.value = false;
      sendNotification(notification.code, notification.message);
    };

    const closeConfirmationModal = () => {
      showConfirmationModal.value = false;
    };

    const updateLicense = async () => {
      closeConfirmationModal();
      loading.value = true;
      // const code = '3649695F'; // equestrianLicense.value.pegase_license_code
      const response = await renewCustomerLicense(customerObject.value.uuid, equestrianLicense.value.data.pegase_license_code);
      const notification = { code: 'error', message: 'Erreur lors du renouvellement de la licence' };

      if (response.success) {
        notification.code = 'success';
        notification.message = 'Licence renouvelée avec succès';
        equestrianLicense.value = computeLicenseResponseBody(response);
      }
      loading.value = false;
      sendNotification(notification.code, notification.message);
    };

    watch(() => props.customer, async () => {
      customerObject.value = props.customer;
      await getCustomerEquestrianLicense();
    });

    return { equestrianLicense, closeConfirmationModal, updateLicense, showConfirmationModal, createLicense, loading, customerObject, formatDate, lastUpdateDate, refreshLicense };
  }
};
</script>
